/**
 * AdminEtatMateriel.js — Page fusionnée
 * Capacités AdminEtatMateriel (état par produit) +
 * Capacités AdminMateriel (vérification conflits de réservation par date)
 * La route /admin/materiel reste un alias silencieux pointant ici.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Wrench, AlertTriangle, CheckCircle, Calendar, Search } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN   = () => sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
const H       = () => ({ Authorization: `Bearer ${TOKEN()}`, 'Content-Type': 'application/json' });

// ─── États (AdminEtatMateriel) ────────────────────────────────────────────
const ETATS = {
  tres_bon:    { label: 'Très bon',    color: 'var(--admin-green)',  bg: 'var(--admin-green-dim)',  emoji: '✅' },
  bon:         { label: 'Bon',         color: '#84CC16',             bg: 'rgba(132,204,22,0.1)',    emoji: '🟢' },
  moyen:       { label: 'Moyen',       color: 'var(--admin-amber)',  bg: 'var(--admin-amber-dim)',  emoji: '🟡' },
  maintenance: { label: 'Maintenance', color: '#F97316',             bg: 'rgba(249,115,22,0.1)',    emoji: '🟠' },
  hs:          { label: 'Hors service',color: 'var(--admin-red)',    bg: 'var(--admin-red-dim)',    emoji: '🔴' },
};

export default function AdminEtatMateriel() {
  // ── État onglets ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('etats'); // etats | conflits

  // ── Onglet 1 : État par produit ───────────────────────────────────────────
  const [produits, setProduits] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(null);
  const [filtre,   setFiltre]   = useState('all');
  const [notes,    setNotes]    = useState({});

  // ── Onglet 2 : Vérification conflits de réservation ───────────────────────
  // (capacité issue de AdminMateriel)
  const [conflitDate,    setConflitDate]    = useState('');
  const [conflitResults, setConflitResults] = useState(null);
  const [conflitLoading, setConflitLoading] = useState(false);
  // Calendrier des réservations (AdminMateriel)
  const [calendar, setCalendar] = useState(null);
  const [calLoading, setCalLoading] = useState(false);

  // ── Chargement état matériel ───────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/exploitation/materiel/etats`, { headers: H() });
      const d   = await res.json();
      setProduits(d.produits || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Charger le calendrier des réservations (onglet conflits)
  const loadCalendar = useCallback(async () => {
    setCalLoading(true);
    try {
      const now = new Date();
      const res = await fetch(
        `${BACKEND}/api/admin/catalogue/location/calendar?mois=${now.getMonth()+1}&annee=${now.getFullYear()}`,
        { headers: H() }
      );
      if (res.ok) setCalendar(await res.json());
    } catch { /* silencieux */ }
    finally { setCalLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'conflits') loadCalendar();
  }, [activeTab, loadCalendar]);

  // ── Mise à jour état d'un produit ─────────────────────────────────────────
  async function updateEtat(productId, etat) {
    setSaving(productId);
    try {
      await fetch(`${BACKEND}/api/admin/exploitation/materiel/${productId}/etat`, {
        method: 'PATCH', headers: H(),
        body: JSON.stringify({ product_id: productId, etat, notes: notes[productId] || '' }),
      });
      setProduits(prev => prev.map(p => p.id === productId ? { ...p, etat } : p));
      toast.success(`${ETATS[etat]?.emoji} État mis à jour : ${ETATS[etat]?.label}`);
    } catch { toast.error('Erreur mise à jour'); }
    finally { setSaving(null); }
  }

  // ── Vérification conflits de réservation ──────────────────────────────────
  async function checkConflits(date) {
    if (!date) return;
    setConflitLoading(true);
    try {
      const productIds = produits.map(p => p.id);
      const res = await fetch(`${BACKEND}/api/materiel/verifier-conflit`, {
        method: 'POST', headers: H(),
        body: JSON.stringify({ date_evenement: date, product_ids: productIds }),
      });
      if (res.ok) setConflitResults(await res.json());
      else {
        // Fallback via l'endpoint booking
        const res2 = await fetch(`${BACKEND}/api/booking/check-availability`, {
          method: 'POST', headers: H(),
          body: JSON.stringify({ product_ids: productIds, date_start: date, date_end: date }),
        });
        const d2 = await res2.json();
        setConflitResults({
          conflict: !d2.all_available,
          message: d2.message,
          conflits: (d2.products || []).filter(p => !p.disponible).map(p => ({
            product_nom: p.product_id,
            order_numero: p.conflicts?.[0]?.quote_numero || '?',
          })),
        });
      }
    } catch { toast.error('Erreur vérification'); }
    finally { setConflitLoading(false); }
  }

  // ── Données dérivées ──────────────────────────────────────────────────────
  const filtered = filtre === 'all'
    ? produits
    : produits.filter(p => (p.etat || 'tres_bon') === filtre);

  const stats = Object.entries(ETATS).map(([k, v]) => ({
    ...v, key: k,
    count: produits.filter(p => (p.etat || 'tres_bon') === k).length,
  }));

  const nbAlerte = produits.filter(p => ['maintenance','hs'].includes(p.etat)).length;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div data-testid="admin-etat-materiel">
      {/* Header cockpit */}
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Produits</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
              letterSpacing: '-.6px', color: 'var(--admin-text)' }}>État du matériel</h2>
            <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>
              Statut par produit · Vérification des conflits de réservation
            </p>
          </div>
          <button onClick={load}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 13px',
              borderRadius: 10, border: '1px solid var(--admin-border)',
              background: 'var(--admin-surface)', color: 'var(--admin-muted)',
              fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
            <RefreshCw size={13}/> Actualiser
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--admin-border-soft)', marginBottom: 16 }}>
        {[
          { k: 'etats',    l: `État des produits (${produits.length})`, icon: <Wrench size={13}/> },
          { k: 'conflits', l: 'Vérifier conflits',                       icon: <Calendar size={13}/> },
        ].map(t => (
          <button key={t.k} onClick={() => setActiveTab(t.k)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 14px',
              fontSize: 13.5, fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none',
              color: activeTab === t.k ? 'var(--admin-text)' : 'var(--admin-muted)',
              borderBottom: activeTab === t.k ? '2px solid var(--admin-teal)' : '2px solid transparent',
              marginBottom: -1 }}>
            {t.icon} {t.l}
            {t.k === 'etats' && nbAlerte > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 20,
                background: 'var(--admin-red-dim)', color: 'var(--admin-red)', marginLeft: 4 }}>
                {nbAlerte} ⚠️
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ════ ONGLET 1 : ÉTAT DES PRODUITS ════ */}
      {activeTab === 'etats' && (
        <div>
          {/* Chips statut */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 6, marginBottom: 16 }}>
            {[{key:'all',label:'Tous',color:'var(--admin-teal)',emoji:'📦',count:produits.length},...stats].map(s => (
              <button key={s.key} onClick={() => setFiltre(s.key)}
                style={{ padding: '10px 6px', borderRadius: 12, cursor: 'pointer', textAlign: 'center',
                  background: filtre === s.key ? s.color : 'var(--admin-surface)',
                  border: `1px solid ${s.color}40`,
                  transition: 'background .15s', border: 'none' }}>
                <p style={{ fontSize: 18, marginBottom: 4 }}>{s.emoji}</p>
                <p style={{ fontFamily: 'var(--admin-font-display)', fontSize: 20, fontWeight: 700,
                  color: filtre === s.key ? (s.key==='all'?'#0B1E20':'#FFF') : s.color }}>{s.count}</p>
                <p style={{ fontSize: 10, fontWeight: 600,
                  color: filtre === s.key ? 'rgba(255,255,255,0.8)' : 'var(--admin-muted)' }}>{s.label}</p>
              </button>
            ))}
          </div>

          {/* Alerte maintenance/hs */}
          {nbAlerte > 0 && (
            <div className="adm-card" style={{ padding: '12px 14px', marginBottom: 12,
              background: 'var(--admin-red-dim)', borderColor: 'rgba(235,92,92,.3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={15} style={{ color: 'var(--admin-red)' }}/>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--admin-red)' }}>
                  {nbAlerte} produit(s) nécessitant attention
                </p>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {produits.filter(p => ['maintenance','hs'].includes(p.etat)).map(p => (
                  <span key={p.id} style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px',
                    borderRadius: 7, background: ETATS[p.etat]?.bg, color: ETATS[p.etat]?.color }}>
                    {ETATS[p.etat]?.emoji} {p.nom?.slice(0,30)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Liste produits */}
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[1,2,3].map(i => <div key={i} className="adm-card" style={{ height: 68, background: 'var(--admin-surface2)' }}/>)}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {filtered.map(prod => {
                const etat = prod.etat || 'tres_bon';
                const conf = ETATS[etat] || ETATS.tres_bon;
                return (
                  <div key={prod.id} className="adm-card" style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      {/* Infos produit */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--admin-text)', marginBottom: 2 }}>{prod.nom}</p>
                        <p style={{ fontSize: 11.5, color: 'var(--admin-muted)' }}>{prod.categorie}</p>
                        {prod.etat_notes && (
                          <p style={{ fontSize: 11, color: 'var(--admin-amber)', marginTop: 3, fontStyle: 'italic' }}>📝 {prod.etat_notes}</p>
                        )}
                      </div>
                      {/* Sélecteur état */}
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {Object.entries(ETATS).map(([key, v]) => (
                          <button key={key} onClick={() => updateEtat(prod.id, key)}
                            disabled={saving === prod.id}
                            style={{ padding: '5px 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                              background: etat === key ? v.color : v.bg,
                              color: etat === key ? '#FFF' : v.color,
                              border: 'none', opacity: saving === prod.id ? .6 : 1,
                              transform: etat === key ? 'scale(1.05)' : 'scale(1)', transition: 'transform .1s' }}>
                            {v.emoji} {v.label}
                          </button>
                        ))}
                      </div>
                      {/* Note */}
                      <input type="text" placeholder="Note..."
                        value={notes[prod.id] ?? prod.etat_notes ?? ''}
                        onChange={e => setNotes(p => ({ ...p, [prod.id]: e.target.value }))}
                        style={{ width: 140, padding: '6px 10px', borderRadius: 9, fontSize: 12,
                          border: '1px solid var(--admin-border)', background: 'var(--admin-surface2)',
                          color: 'var(--admin-text)', outline: 'none' }}
                      />
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--admin-faint)' }}>
                  <CheckCircle size={32} style={{ margin: '0 auto 10px', opacity: .2 }}/>
                  <p>Aucun produit dans cet état</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ════ ONGLET 2 : VÉRIFICATION CONFLITS ════ */}
      {/* Capacité issue de AdminMateriel — anti-double réservation par date */}
      {activeTab === 'conflits' && (
        <div>
          <p style={{ fontSize: 13.5, color: 'var(--admin-muted)', marginBottom: 16 }}>
            Vérifiez si des produits sont déjà réservés pour une date donnée.
          </p>

          {/* Sélecteur date */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
            <input type="date"
              value={conflitDate}
              onChange={e => { setConflitDate(e.target.value); setConflitResults(null); }}
              min={new Date().toISOString().slice(0, 10)}
              style={{ flex: 1, padding: '10px 14px', borderRadius: 10,
                background: 'var(--admin-surface)', border: '1px solid var(--admin-border)',
                color: 'var(--admin-text)', fontSize: 13.5, outline: 'none' }}
            />
            <button
              onClick={() => checkConflits(conflitDate)}
              disabled={!conflitDate || conflitLoading}
              style={{ padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                background: conflitDate ? 'var(--admin-gold)' : 'var(--admin-surface2)',
                color: conflitDate ? '#2a1d05' : 'var(--admin-faint)',
                border: 'none', cursor: conflitDate ? 'pointer' : 'not-allowed' }}>
              {conflitLoading ? '...' : '🔍 Vérifier'}
            </button>
          </div>

          {/* Résultats */}
          {conflitResults && (
            <div className="adm-card" style={{
              padding: '14px 16px',
              background: conflitResults.conflict ? 'var(--admin-red-dim)' : 'var(--admin-green-dim)',
              borderColor: conflitResults.conflict ? 'rgba(235,92,92,.3)' : 'rgba(63,208,139,.3)',
            }}>
              <p style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 8,
                color: conflitResults.conflict ? 'var(--admin-red)' : 'var(--admin-green)' }}>
                {conflitResults.conflict ? '⚠️' : '✅'} {conflitResults.message}
              </p>
              {conflitResults.conflits?.map((c, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--admin-muted)', marginTop: 4,
                  display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--admin-red)' }}>●</span>
                  <strong>{c.product_nom}</strong> — Commande : {c.order_numero}
                </div>
              ))}
            </div>
          )}

          {/* Calendrier réservations */}
          <div style={{ marginTop: 24 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-gold)', marginBottom: 12 }}>
              📅 Réservations ce mois
            </p>
            {calLoading ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--admin-faint)' }}>Chargement...</div>
            ) : (calendar?.reservations?.length === 0 || !calendar) ? (
              <div className="adm-card" style={{ padding: '30px 20px', textAlign: 'center' }}>
                <p style={{ color: 'var(--admin-faint)', fontSize: 13 }}>Aucune réservation ce mois</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {(calendar?.reservations || []).map((r, i) => (
                  <div key={i} className="adm-card" style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <div>
                        <p style={{ fontFamily: 'var(--admin-font-display)', fontWeight: 700,
                          fontSize: 12, color: 'var(--admin-gold)', marginBottom: 3, letterSpacing: '.04em' }}>
                          {r.date}
                        </p>
                        <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--admin-text)', marginBottom: 2 }}>{r.client}</p>
                        <p style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{r.adresse}</p>
                        {r.items && (
                          <p style={{ fontSize: 11.5, color: 'var(--admin-faint)', marginTop: 3 }}>{r.items.join(', ')}</p>
                        )}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 7,
                        background: 'var(--admin-gold-dim)', color: 'var(--admin-gold)' }}>
                        {r.statut}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
